import {useEffect, useMemo, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {notify} from "@/components/Notifications.tsx";
import {Tag, useSaveTag} from "@/hooks/useTags.ts";
import {getErrMsg} from "@/lib/errors.ts";

type Props = {
    open: boolean;
    initial?: Partial<Tag> | null;
    onClose: () => void;
};

export default function TagUpsertDialog({open, initial, onClose}: Props) {
    const isEdit = Boolean(initial?.id);

    const [name, setName] = useState(initial?.name ?? "");
    const [description, setDescription] = useState(initial?.description ?? "");
    const [touched, setTouched] = useState(false);

    const {mutateAsync: save, isPending} = useSaveTag();

    useEffect(() => {
        if (open) {
            setName(initial?.name ?? "");
            setDescription(initial?.description ?? "");
            setTouched(false);
        }
    }, [open, initial]);

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!name.trim()) e.name = "Name is required.";
        if (name.length > 100) e.name = "Keep the name under 100 characters.";
        if (description && description.length > 500) e.description = "Max 500 characters.";
        return e;
    }, [name, description]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (Object.keys(errors).length) return;

        try {
            await save({
                id: initial?.id as string | undefined,
                name,
                description
            });
            onClose();
            notify.success(`Tag ${isEdit ? "updated" : "created"} successfully.`);
        } catch (e) {
            notify.error(getErrMsg(e, `Failed to ${isEdit ? "update" : "create"} tag. Please try again.`));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-md sm:max-w-lg md:max-w-xl transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:p-6 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95"
                    >
                        <DialogTitle as="h3" className="text-lg font-semibold text-blue-900">
                            {isEdit ? "Edit Tag" : "New Tag"}
                        </DialogTitle>

                        <form onSubmit={onSubmit} noValidate>
                            <div className="mt-6 grid grid-cols-1 gap-6">
                                <div>
                                    <label
                                        htmlFor="cat-name"
                                        className="block text-sm font-medium text-blue-900">
                                        Name
                                    </label>
                                    <input
                                        id="tag-name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.name}
                                        aria-describedby={errors.name ? "tag-name-error" : undefined}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                        placeholder="e.g., Sports"
                                    />
                                    {touched && errors.name && (
                                        <p id="tag-name-error" className="mt-1 text-xs text-red-600">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="tag-desc" className="block text-sm font-medium text-blue-900">
                                        Description
                                    </label>
                                    <textarea
                                        id="tag-desc"
                                        rows={4}
                                        value={description ?? ""}
                                        onChange={(e) => setDescription(e.target.value)}
                                        aria-invalid={touched && !!errors.description}
                                        aria-describedby={errors.description ? "tag-desc-error" : undefined}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                        placeholder="Short description (optional)"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Optional. Keep it concise.</p>
                                    {touched && errors.description && (
                                        <p id="tag-desc-error" className="mt-1 text-xs text-red-600">
                                            {errors.description}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-6 flex items-center justify-end gap-x-3">
                                <button type="button" onClick={onClose}>Cancel</button>
                                <button
                                    type="submit"
                                    className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:opacity-60"
                                    disabled={isPending}>
                                    {isPending ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save" : "Create"}
                                </button>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
